import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import EnterpriseAPI from '../components/corporate/EnterpriseAPI';
import HammurabiContract from '../components/corporate/HammurabiContract';
import EscrowFlow from '../components/corporate/EscrowFlow';
import YieldCalculator from '../components/corporate/YieldCalculator';
import CompliancePanel from '../components/economics/CompliancePanel';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { Link } from 'react-router-dom';

export default function CorporateGateway() {
  const [activeTab, setActiveTab] = useState('escrow');

  const tabs = [
    { id: 'escrow', label: 'Escrow Flow', icon: FiIcons.FiGitBranch, color: 'cyan' },
    { id: 'contract', label: 'Hammurabi Contract', icon: FiIcons.FiShield, color: 'fuchsia' },
    { id: 'api', label: 'Corporate API Spec', icon: FiIcons.FiTerminal, color: 'amber' },
  ];

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 max-w-6xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <div className="flex items-center gap-2 text-cyan-400 mb-3">
          <SafeIcon icon={FiIcons.FiServer} />
          <span className="text-xs font-bold tracking-widest uppercase">/// Corporate Purchase Gateway</span>
        </div>
        <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tighter text-zinc-100 mb-4">
          Data Utility <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-fuchsia-400">
            Infrastructure
          </span>
        </h1>
        <p className="text-sm text-zinc-400 max-w-2xl leading-relaxed">
          Enterprises don't buy "your data"—they buy query rights against a differentially private aggregate.
          Zero raw data access. Zero re-identification risk. Cryptographic proof of consent and quality.
        </p>
      </motion.div>

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        
        {/* Left Col: Tabs & Content */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Tab Navigation */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-zinc-800 hide-scrollbar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-xs font-bold tracking-widest uppercase whitespace-nowrap transition-all border-b-2 ${
                  activeTab === tab.id
                    ? `border-${tab.color}-400 text-${tab.color}-400 bg-${tab.color}-500/10`
                    : 'border-transparent text-zinc-500 hover:text-zinc-300 hover:bg-zinc-900/50'
                }`}
              >
                <SafeIcon icon={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="min-h-[500px]">
            <AnimatePresence mode="wait">
              {activeTab === 'escrow' && (
                <motion.div
                  key="escrow"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiGitBranch} /> The BSM-C Data Pipeline
                  </h2>
                  <EscrowFlow />
                  <div className="mt-8 bg-zinc-900/50 border border-zinc-800 p-5 rounded-lg text-sm text-zinc-400 leading-relaxed">
                    <span className="text-cyan-400 font-bold uppercase tracking-widest text-[10px] block mb-2">The Mechanism</span>
                    Raw behavioral data is generated on the user's device (Bare Metal Genesis), stripped of PII via the Doppelganger Sandbox, and locked into the Hammurabi Escrow. Corporate buyers query the Eduba Cache to receive high-signal, differentially private aggregates.
                  </div>
                </motion.div>
              )}

              {activeTab === 'contract' && (
                <motion.div
                  key="contract"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiShield} /> Core Settlement Logic
                  </h2>
                  <HammurabiContract />
                </motion.div>
              )}

              {activeTab === 'api' && (
                <motion.div
                  key="api"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiTerminal} /> Enterprise Integration Points
                  </h2>
                  <EnterpriseAPI />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Right Col: Yield & Compliance */}
        <div className="space-y-6">
          <h2 className="text-xs font-bold tracking-widest text-zinc-500 uppercase mb-4 flex items-center gap-2">
            <SafeIcon icon={FiIcons.FiTrendingUp} /> Node Yield Projection
          </h2>
          <YieldCalculator />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-zinc-900/30 border border-zinc-800 rounded-lg p-5"
          >
            <div className="text-xs font-bold text-zinc-100 mb-3 uppercase tracking-wide">Enterprise Pricing</div>
            <div className="space-y-2 text-[11px]">
              <div className="flex justify-between">
                <span className="text-zinc-500">High-fidelity cohort req</span>
                <span className="text-cyan-400 font-mono">$0.02/query</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Verified predictions</span>
                <span className="text-fuchsia-400 font-mono">$0.05/prediction</span>
              </div>
              <div className="flex justify-between">
                <span className="text-zinc-500">Clean training data</span>
                <span className="text-emerald-400 font-mono">$50/GB</span>
              </div>
            </div>
          </motion.div>

          <CompliancePanel />
        </div>
      </div>

      {/* Critical Distinction */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="p-8 bg-gradient-to-br from-zinc-900/60 to-zinc-950 border border-cyan-500/20 rounded-lg mb-12"
      >
        <div className="text-xs text-cyan-400 tracking-widest uppercase mb-3">/// The Critical Distinction</div>
        <p className="text-sm md:text-base text-zinc-300 leading-relaxed max-w-3xl mx-auto">
          Users aren't selling <span className="text-rose-400 line-through">access to themselves</span> (the surveillance model).
          <br /><br />
          They're selling <span className="text-cyan-400 font-bold">proof that a high-signal cohort exists</span> and 
          <span className="text-amber-400 font-bold"> verified predictions about that cohort's preferences</span>—without ever exposing individual records.
        </p>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="flex flex-col sm:flex-row items-center justify-center gap-4"
      >
        <Link
          to="/showroom"
          className="px-8 py-4 bg-cyan-500 text-zinc-950 font-bold uppercase tracking-widest text-sm rounded hover:box-glow-cyan transition-all flex items-center gap-3"
        >
          <SafeIcon icon={FiIcons.FiCpu} />
          Deploy Sovereign Node
        </Link>
      </motion.div>
    </div>
  );
}